import {
  EndpointService,
  type APIPaginatedResponse,
  type ID,
} from "@/types/api/api";
import type { IAPI } from "../APIService";
import {
  useInfiniteQuery,
  type UseInfiniteQueryResult,
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { buildQueryKey } from "@/utils/query";
import type { AxiosResponse } from "axios";
import type { IModifierWithAllergensAndDietaryRequirementsAsNumbers } from "@/components/screens/Operations/Modifiers/Edit/Types/EditModifierTypes";

export enum IModifierListOrderByParams {
  InternalName = "internal_name",
  Name = "name",
  Value = "value",
  Order = "order",
  NumberOfModifierCategories = "number_of_modifier_categories",
  InStock = "in_stock",
  Active = "active",
}

export interface IModifier {
  updated_at: string;
  id: number;
  active: boolean;
  allergens: Array<{
    id: number;
    name: string;
    order: number;
  }>;
  dietary_requirements: Array<{
    id: number;
    name: string;
    order: number;
  }>;
  master_modifier_id: number | null;
  discount_applicable: boolean;
  alt_name: string | null;
  description: string;
  internal_name: string;
  name: string;
  cost_price: number;
  is_default_option: boolean;
  value: number;
  order: number;
  number_of_modifier_categories: number;
  in_stock: boolean;
  created_at: string;
}

export interface ICreateModifierParams {
  active?: boolean;
  allergens?: number[];
  cost_price?: number | null;
  description?: string;
  dietary_requirements?: number[];
  discount_applicable?: boolean;
  in_stock?: boolean;
  internal_name?: string | null;
  name: string;
  value: number;
}

export interface IBulkCreateModifierRelationshipsParams {
  modifier_id?: ID;
  menu_item_menu_categories?: Array<{
    menu_item_id: number | string;
    menu_category_id: number | string;
  }>;
  modifier_category_menu_items?: Array<{
    menu_item_id: number | string;
    modifier_category_id: number | string;
  }>;
  modifier_modifier_categories?: Array<{
    modifier_category_id: number | string;
    modifier_id: number | string;
  }>;
}

export interface IBulkCreateModifierRelationshipsResponse {
  modifier_modifier_categories: Array<{
    cost_price: null;
    id: number;
    is_default_option: null;
    order: null;
    modifier_id: number;
    value: null;
    modifier_category_id: number;
  }>;
  modifier_category_menu_items: Array<{
    selection_required: null;
    id: number;
    order: null;
    modifier_category_id: number;
    menu_item_id: number;
  }>;
  menu_item_menu_categories: Array<{
    cost_price: null;
    id: number;
    promotion_value: null;
    order: null;
    value: null;
    menu_item_id: number;
    is_promoted: null;
    menu_category_id: number;
  }>;
}

export interface IModifierListResponse
  extends APIPaginatedResponse<IModifier> {}

export interface IModifierListParams {
  page?: number;
  page_size?: number;
  search?: string;
  order_by?: string;
  active?: boolean;
  exclude_modifier_categories?: Array<number>;
}

export interface IListModifiersReponseAll {
  pages: IModifierListResponse[];
  pageParams: number[];
}

export interface IListModifierSetsRelationshipsParams {
  modifier_id: ID;
}

export interface IListModifierSetsRelationship {
  modifier_category_id: number;
  is_default_option: boolean;
  id: number;
  modifier_category: {
    internal_name: string;
    number_of_modifiers: number;
    id: number;
    order: number;
    category_type: number;
    name: string;
    selection_required: boolean;
    active: boolean;
  };
  value: number;
  modifier: {
    internal_name: string;
    description: string;
    is_default_option: boolean;
    id: number;
    order: number;
    in_stock: boolean;
    value: number;
    name: string;
    active: boolean;
    cost_price: number;
  };
  modifier_id: number;
  order: number;
  cost_price: number;
}

export interface IListModifierSetsRelationshipsResponse
  extends APIPaginatedResponse<IListModifierSetsRelationship> {}

export interface IDeleteModifierModifierSetRelationshipParamsBase {
  relationship_id: ID;
  modifier_id: ID;
}

export interface IUpdateModifierParams extends ICreateModifierParams {
  id: ID;
}

export interface IDeleteModifierParams {
  id: ID;
}

export interface IModifiersService {
  listAll: (
    params: IModifierListParams,
  ) => UseInfiniteQueryResult<IListModifiersReponseAll, Error>;
  list: (
    params: IModifierListParams,
  ) => UseQueryResult<IModifierListResponse, Error>;

  // CRUD
  create: () => UseMutationResult<IModifier, Error, ICreateModifierParams>;
  retrieve: (
    id: ID,
  ) => UseQueryResult<
    IModifierWithAllergensAndDietaryRequirementsAsNumbers,
    Error
  >;
  update: () => UseMutationResult<IModifier, Error, IUpdateModifierParams>;
  delete: () => UseMutationResult<boolean, Error, IDeleteModifierParams>;

  // List Relationships
  listModifierSetsRelationships: (
    params: IListModifierSetsRelationshipsParams,
  ) => UseQueryResult<Array<IListModifierSetsRelationship>, Error>;

  // Relationship Mutations
  bulkCreateModifierRelationships: () => UseMutationResult<
    IBulkCreateModifierRelationshipsResponse,
    Error,
    IBulkCreateModifierRelationshipsParams
  >;

  // Delete Relationships
  deleteModifierModifierSetRelationships: () => UseMutationResult<
    boolean,
    Error,
    IDeleteModifierModifierSetRelationshipParamsBase
  >;
}

export class ModifiersService implements IModifiersService {
  api: IAPI;

  constructor(api: IAPI) {
    this.api = api;
  }

  listAll(
    params: IModifierListParams,
  ): UseInfiniteQueryResult<IListModifiersReponseAll, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== null),
    );
    return useInfiniteQuery({
      queryKey: buildQueryKey({
        queryKey: ["organisation", "modifiers", "all"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async ({ pageParam = 1 }) => {
        const { data } = await this.api.get<
          AxiosResponse<IModifierListResponse>
        >("/menu/modifiers/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: { ...updatedParams, page: pageParam, page_size: 100 },
        });

        return data;
      },
      getNextPageParam: (lastPage: IModifierListResponse) => {
        if (lastPage.next) {
          const url = new URL(lastPage.next);
          return Number.parseInt(url.searchParams.get("page") || "1", 10);
        }
        return undefined;
      },
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      initialPageParam: 1,
    });
  }

  list(
    params: IModifierListParams,
  ): UseQueryResult<IModifierListResponse, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) => value !== null && value !== undefined,
      ),
    );

    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["operations", "modifiers"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<
          AxiosResponse<IModifierListResponse>
        >("/menu/modifiers/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: updatedParams,
        });

        return {
          results: data.results,
          count: data.count,
          next: data.next,
          previous: data.previous,
        };
      },
    });
  }

  // CRUD

  create(): UseMutationResult<IModifier, Error, ICreateModifierParams> {
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "modifiers", "create"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        const { data } = await this.api.post<AxiosResponse<IModifier>>(
          "/menu/me/modifiers/",
          {
            endpointService: EndpointService.Core,
            venueSpecific: false,
            params: params,
          },
        );

        return data;
      },

      // TODO: Implement Functionality when doing edit menu workflow
      onMutate: async (newMenu) => {},
      onError: (err, newMenu, context) => {},
      onSettled: () => {},
    });
  }

  retrieve(
    id: ID,
  ): UseQueryResult<
    IModifierWithAllergensAndDietaryRequirementsAsNumbers,
    Error
  > {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["operations", "modifiers", "retrieve", id],
        venueID: null,
        params: {},
      }),
      queryFn: async () => {
        const { data } = await this.api.get<AxiosResponse<IModifier>>(
          `/menu/modifiers/${id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );

        return {
          ...data,
          allergens: data.allergens.map((allergen) => allergen.id),
          dietary_requirements: data.dietary_requirements.map(
            (dietaryRequirement) => dietaryRequirement.id,
          ),
        };
      },
    });
  }

  update(): UseMutationResult<IModifier, Error, IUpdateModifierParams> {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "modifiers", "update"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        const { id, ...restParams } = params;
        const { data } = await this.api.patch<AxiosResponse<IModifier>>(
          `/menu/me/modifiers/${id}/`,
          {
            endpointService: EndpointService.Core,
            venueSpecific: false,
            params: restParams,
          },
        );

        return data;
      },
      onMutate: async (newModifier) => {},
      onError: (err, newModifier, context) => {},
      onSettled: (data, error, variables) => {
        queryClient.invalidateQueries({
          queryKey: [
            "operations",
            "modifiers",
            "retrieve",
            variables.id.toString(),
          ],
        });
      },
    });
  }

  delete(): UseMutationResult<boolean, Error, IDeleteModifierParams> {
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "modifiers", "delete"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        await this.api.delete<AxiosResponse<boolean>>(
          `/menu/me/modifiers/${params.id}/`,
          {
            endpointService: EndpointService.Core,
            venueSpecific: false,
          },
        );

        return true;
      },
      onMutate: async (newDeleteModifierParams) => {},
      onError: (err, newDeleteModifierParams, context) => {
        return false;
      },
      onSettled: () => {},
    });
  }

  // List Relationships
  listModifierSetsRelationships(
    params: IListModifierSetsRelationshipsParams,
  ): UseQueryResult<Array<IListModifierSetsRelationship>, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["operations", "modifiers", "listModifierSetsRelationships"],
        venueID: null,
        params: params,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<
          AxiosResponse<IListModifierSetsRelationshipsResponse>
        >("/menu/me/modifier_modifiercategories/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: { ...params, page_size: 50, page: 1 },
        });

        return data.results;
      },
    });
  }

  // Relationship Mutations
  bulkCreateModifierRelationships(): UseMutationResult<
    IBulkCreateModifierRelationshipsResponse,
    Error,
    IBulkCreateModifierRelationshipsParams
  > {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: [
          "operations",
          "modifiers",
          "bulkCreateModifierRelationships",
        ],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        const { modifier_id, ...restParams } = params;
        const { data } = await this.api.post<
          AxiosResponse<IBulkCreateModifierRelationshipsResponse>
        >("/menu/me/relationships/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: restParams,
        });

        return data;
      },
      onMutate: async (newBulkCreateModifierRelationshipsParams) => {},
      onError: (err, newBulkCreateModifierRelationshipsParams, context) => {},
      onSettled: (data, error, variables) => {
        if (variables.modifier_id) {
          queryClient.invalidateQueries({
            queryKey: [
              "operations",
              "modifiers",
              "listModifierSetsRelationships",
            ],
          });
        }
      },
    });
  }

  // Delete Relationships
  deleteModifierModifierSetRelationships(): UseMutationResult<
    boolean,
    Error,
    IDeleteModifierModifierSetRelationshipParamsBase
  > {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: [
          "operations",
          "modifiers",
          "deleteModifierModifierSetRelationships",
        ],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        await this.api.delete<AxiosResponse<boolean>>(
          `/menu/me/modifier_modifiercategories/${params.relationship_id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );

        return true;
      },
      onMutate: async (newDeleteRelationshipParams) => {},
      onError: (err, newDeleteRelationshipParams, context) => {
        return false;
      },
      onSettled: (data, error, variables) => {
        if (variables.modifier_id) {
          queryClient.invalidateQueries({
            queryKey: [
              "operations",
              "modifiers",
              "listModifierSetsRelationships",
            ],
          });
        }
      },
    });
  }
}
